/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { headerSearchSX } from '@/components/content/Header/styles/search';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { SEARCH_AUTOSUGGEST_MAX_RESULT_DISPLAY } from '@/data/constants/search';
import { useSearchNavigation } from '@/data/Content/SearchNavigation';
import { useLocalization } from '@/data/Localization';
import { SuggestOption } from '@/data/types/Search';
import { comparator } from '@/utils/search';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { TranslationTable } from 'integration/generated/translations';
import { uniqWith } from 'lodash';
import { FC, useMemo,useRef  } from 'react';

type Option = {
	label: string;
	href?: string;
	identifier: string;
};

export const HeaderSearch: FC<{ mobile?: boolean }> = ({ mobile }) => {
	const { searchValue, suggest, onInputChange, onSubmit } = useSearchNavigation();
	const SearchNLS = useLocalization('SearchBar');
	const uniqueId = `type-ahead-site-search-${mobile ? 'mobile' : 'desktop'}`;
	const options = useMemo<SuggestOption[]>(
		() =>
			suggest
				.map(({ identifier, entry }) =>
					identifier && entry
						?entry?.filter
							(e=>identifier!=="Brand"?true: e?.label?.toLowerCase()?.includes(searchValue?.toLowerCase()))
						?.slice(0,SEARCH_AUTOSUGGEST_MAX_RESULT_DISPLAY)
						?.map(({ label, href }) => ({ label, href, identifier })) || []
						: []
				)
				.flat(1),
		[suggest]
	);

	const uniqueLabelOptions = useMemo<SuggestOption[]>(
		() => {
			let suggOtions = uniqWith(options, comparator);
			suggOtions = suggOtions.map(sugg=>{
				const idNLS = SearchNLS[sugg.identifier as keyof TranslationTable['SearchBar']].t();
				return {
					...sugg,
					identifierNLS:idNLS!==EMPTY_STRING?idNLS:sugg.identifier
				}
			})
			return suggOtions;
		},
		[options]
	);
	const enterPressed = useRef(false);
	return (
		<Autocomplete
			freeSolo
			disableClearable
			// onChange={(_, item) => onSubmit(typeof item === 'string' ? { label: item } : item)}
			onChange={(_, item) => {
                if (!enterPressed.current) {
                    onSubmit(typeof item === 'string' ? { label: item} : item);
                }
                enterPressed.current = false; 
            }}
			onInputChange={onInputChange}
			options={uniqueLabelOptions}
			groupBy={(option) => option.identifierNLS??option.identifier}
			filterOptions={(options) => options}
			sx={headerSearchSX({ isMobile: mobile })}
			id={uniqueId}
			ListboxProps={{
				style:{
					maxHeight:'unset'
				}
			}}
			renderGroup={(params) => (
				<Box key={params.key}>
				  <Typography fontWeight='bold' p={2}>
					{params.group}
				  </Typography>
				  {params.children}
				</Box>
			  )}
			renderInput={({ inputProps, ...params }) => (
				<TextField
					required
					inputProps={{ ...inputProps, 'data-testid': uniqueId }}
					variant='outlined'
					{...params}
					InputProps={{
						...params.InputProps,
						placeholder: SearchNLS.SearchField.t(),
						slotProps: {
							input: { role: undefined, 'aria-expanded': undefined },
						},
						size: 'small',
						type: 'input',
						onKeyDown: (e) => {
                            if (e.code === 'Enter') {
                                e.preventDefault();
                                enterPressed.current = true; 
                                if (e.currentTarget.value) {
                                    onSubmit({ label: e.currentTarget.value});
                                }
                            }
                        },
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									aria-label={SearchNLS.SearchIconLabel.t()}
									onClick={() => onSubmit({ label: searchValue })}
									edge="end"
									size ="medium"
								>
									<SearchIcon />
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			)}
		/>
	);
};
